// Dessin procédural déterministe des tanks et des formes (aucun asset externe).
// Les tanks sont dessinés pour un rayon de référence puis mis à l'échelle ; la hitbox reste le cercle serveur.
import { Graphics } from 'pixi.js';
import type { ClassNode } from '../../../../shared/catalog/classes';
import type { BarrelDef } from '../../../../shared/catalog/weapons';
import type { ShapeDef } from '../../../../shared/shapes';

export const ART_R = 24;

export interface Palette {
  hull: number;
  plate: number;
  tread: number;
  barrel: number;
  band: number;
  outline: number;
}

/** Les cinq styles de base gratuits du dossier (§11). */
export const PALETTES: Record<'atelier' | 'desert' | 'boreal' | 'obsidienne' | 'signal', Palette> = {
  atelier: { hull: 0x8b97a5, plate: 0xa9b4c0, tread: 0x3a414b, barrel: 0x5f6976, band: 0x434b55, outline: 0x22272e },
  desert: { hull: 0xc4a574, plate: 0xd8be92, tread: 0x5a4a35, barrel: 0x8a7552, band: 0x6b5a3f, outline: 0x2e261b },
  boreal: { hull: 0x6f9cc4, plate: 0x93bbdc, tread: 0x2f4357, barrel: 0x4f6f8c, band: 0x3b546b, outline: 0x1b2633 },
  obsidienne: { hull: 0x3a3d44, plate: 0x4d515a, tread: 0x1c1e22, barrel: 0x2a2d33, band: 0x55595f, outline: 0x0d0e10 },
  signal: { hull: 0xd9773a, plate: 0xeb9a62, tread: 0x4a3426, barrel: 0x8c5a3a, band: 0x6a432b, outline: 0x2b1a10 },
};

export const ACCENT_SELF = 0x39d0ff;
export const ACCENT_ENEMY = 0xff5a3c;
export const ACCENT_BOT = 0xffa24a;

/** Caisse ; `decor` 1 = compacte (décor réduit), 2 = lourde (plaques supplémentaires). Hitbox inchangée. */
export function drawHull(g: Graphics, p: Palette, decor = 0): void {
  const R = ART_R;
  g.clear();
  if (decor === 2) {
    for (const side of [-1, 1]) g.roundRect(-0.6 * R, side * 0.86 * R - 0.08 * R, 1.2 * R, 0.16 * R, 0.05 * R).fill(p.band).stroke({ width: 2, color: p.outline });
  }
  // Chenilles décoratives
  for (const side of [-1, 1]) {
    const y = side * 0.66 * R;
    g.roundRect(-0.68 * R, y - 0.19 * R, 1.36 * R, 0.38 * R, 0.12 * R).fill(p.tread).stroke({ width: 2, color: p.outline });
    for (let i = -3; i <= 3; i++) {
      const x = i * 0.18 * R;
      g.moveTo(x, y - 0.17 * R).lineTo(x, y + 0.17 * R).stroke({ width: 1.2, color: p.outline, alpha: 0.6 });
    }
  }
  // Caisse
  g.roundRect(-0.64 * R, -0.52 * R, 1.28 * R, 1.04 * R, 0.18 * R).fill(p.hull).stroke({ width: 2.5, color: p.outline });
  if (decor === 1) return;
  g.roundRect(-0.42 * R, -0.34 * R, 0.84 * R, 0.68 * R, 0.1 * R).fill(p.plate).stroke({ width: 1.5, color: p.outline, alpha: 0.7 });
  // Rivets
  for (const [x, y] of [
    [-0.52, -0.4],
    [0.52, -0.4],
    [-0.52, 0.4],
    [0.52, 0.4],
  ]) g.circle(x * R, y * R, 0.05 * R).fill(p.outline);
  // Nez (indique le sens de la caisse)
  g.poly([0.64 * R, -0.18 * R, 0.76 * R, 0, 0.64 * R, 0.18 * R]).fill(p.plate).stroke({ width: 1.5, color: p.outline });
}

function rot(pts: [number, number][], cos: number, sin: number): number[] {
  const flat: number[] = [];
  for (const [x, y] of pts) flat.push(x * cos - y * sin, x * sin + y * cos);
  return flat;
}

/** Dessine un tube selon sa forme (tube, trappe à drones, mortier, émetteur, lanceur). */
function drawBarrel(g: Graphics, b: BarrelDef, p: Palette, accent: number): void {
  const R = ART_R;
  const a = (b.angle * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const w = b.width * R;
  const L = b.length * R;
  const f = (b.flare ?? 0) * R;
  const off = b.offset * R;
  const look = b.look ?? 'tube';
  const x0 = b.auto ? 0 : 0.15 * R;
  if (look === 'hatch') {
    // Trappe : trapèze qui s'élargit vers l'extérieur.
    g.poly(rot([[x0, off - w * 0.35], [L, off - w / 2 - f], [L, off + w / 2 + f], [x0, off + w * 0.35]], cos, sin)).fill(p.barrel).stroke({ width: 2, color: p.outline });
    g.poly(rot([[L - 0.18 * R, off - w / 2 - f * 0.8], [L, off - w / 2 - f], [L, off + w / 2 + f], [L - 0.18 * R, off + w / 2 + f * 0.8]], cos, sin)).fill(accent).stroke({ width: 1.5, color: p.outline });
    return;
  }
  if (look === 'mortar') {
    g.poly(rot([[x0, off - w * 0.42], [L, off - w / 2], [L, off + w / 2], [x0, off + w * 0.42]], cos, sin)).fill(p.barrel).stroke({ width: 2.5, color: p.outline });
    g.poly(rot([[L - 0.3 * R, off - w / 2 - 0.08 * R], [L, off - w / 2 - 0.08 * R], [L, off + w / 2 + 0.08 * R], [L - 0.3 * R, off + w / 2 + 0.08 * R]], cos, sin)).fill(p.band).stroke({ width: 2, color: p.outline });
    return;
  }
  if (look === 'emitter') {
    g.poly(rot([[x0, off - w / 2], [L, off - w * 0.3], [L, off + w * 0.3], [x0, off + w / 2]], cos, sin)).fill(p.barrel).stroke({ width: 2, color: p.outline });
    const [ex, ey] = [L * cos - off * sin, L * sin + off * cos];
    g.circle(ex, ey, w * 0.42).fill(accent).stroke({ width: 1.5, color: p.outline });
    return;
  }
  if (look === 'trap') {
    // Piège : tube court puis embouchure évasée en queue d'aronde.
    const mid = x0 + (L - x0) * 0.55;
    g.poly(rot([[x0, off - w * 0.38], [mid, off - w * 0.38], [mid, off + w * 0.38], [x0, off + w * 0.38]], cos, sin)).fill(p.barrel).stroke({ width: 2, color: p.outline });
    g.poly(rot([[mid, off - w * 0.38], [L, off - w * 0.62], [L, off + w * 0.62], [mid, off + w * 0.38]], cos, sin)).fill(p.band).stroke({ width: 2, color: p.outline });
    return;
  }
  if (look === 'mine') {
    // Mines : tube large terminé par un dôme rayé (signal de danger).
    g.poly(rot([[x0, off - w * 0.45], [L - w * 0.3, off - w * 0.45], [L - w * 0.3, off + w * 0.45], [x0, off + w * 0.45]], cos, sin)).fill(p.barrel).stroke({ width: 2, color: p.outline });
    const [ex, ey] = [(L - w * 0.3) * cos - off * sin, (L - w * 0.3) * sin + off * cos];
    g.circle(ex, ey, w * 0.5).fill(0xe0b030).stroke({ width: 2, color: p.outline });
    g.circle(ex, ey, w * 0.22).fill(p.outline);
    return;
  }
  if (look === 'launcher') {
    g.poly(rot([[x0, off - w / 2], [L, off - w / 2], [L, off + w / 2], [x0, off + w / 2]], cos, sin)).fill(p.barrel).stroke({ width: 2, color: p.outline });
    for (const k of [-0.25, 0.25]) {
      g.poly(rot([[x0 + 0.1 * R, off + k * w - 1.5], [L - 0.05 * R, off + k * w - 1.5], [L - 0.05 * R, off + k * w + 1.5], [x0 + 0.1 * R, off + k * w + 1.5]], cos, sin)).fill(p.outline);
    }
    return;
  }
  g.poly(rot([[x0, off - w / 2], [L, off - w / 2 - f], [L, off + w / 2 + f], [x0, off + w / 2]], cos, sin)).fill(p.barrel).stroke({ width: 2, color: p.outline });
  const bx = L - 0.22 * R;
  g.poly(rot([[bx, off - w / 2 - f * 0.8], [L, off - w / 2 - f], [L, off + w / 2 + f], [bx, off + w / 2 + f * 0.8]], cos, sin)).fill(p.band).stroke({ width: 1.5, color: p.outline });
}

/** Détails de caisse propres à certaines familles (éperons de collision), dans le repère de la caisse. */
export function drawHullFamily(g: Graphics, cls: ClassNode, p: Palette): void {
  const R = ART_R;
  const body = cls.body;
  if (cls.family !== 'Collision') return;
  // Éperon frontal, plus long pour les classes à élan ou à blindage frontal.
  const len = body.momentum || body.frontArmor ? 0.55 : 0.38;
  g.poly([0.6 * R, -0.42 * R, (0.78 + len) * R, 0, 0.6 * R, 0.42 * R]).fill(p.plate).stroke({ width: 2.5, color: p.outline });
  if (cls.id === 'C09-B2' || cls.id === 'C09-B' || cls.id === 'C09-B1') {
    // Contact périphérique : dents tout autour.
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      g.poly([Math.cos(a - 0.12) * 0.86 * R, Math.sin(a - 0.12) * 0.86 * R, Math.cos(a) * 1.1 * R, Math.sin(a) * 1.1 * R, Math.cos(a + 0.12) * 0.86 * R, Math.sin(a + 0.12) * 0.86 * R]).fill(p.plate).stroke({ width: 1.5, color: p.outline });
    }
  }
  if (body.frontArmor) g.roundRect(0.5 * R, -0.62 * R, 0.16 * R, 1.24 * R, 0.05 * R).fill(p.band).stroke({ width: 2, color: p.outline });
}

export function drawTurret(g: Graphics, cls: ClassNode, p: Palette, accent: number): void {
  const R = ART_R;
  g.clear();
  for (const b of cls.weapon.barrels) if (!b.auto) drawBarrel(g, b, p, accent);
  g.circle(0, 0, 0.5 * R).fill(p.hull).stroke({ width: 2.5, color: p.outline });
  g.circle(0, 0, 0.3 * R).fill(accent).stroke({ width: 1.5, color: p.outline });
  g.circle(0, 0, 0.09 * R).fill(p.outline);
}

/** Tourelles automatiques : petites tourelles dessinées dans leur propre repère (rotation autoAim). */
export function drawAutoTurrets(g: Graphics, cls: ClassNode, p: Palette, accent: number): boolean {
  const R = ART_R;
  g.clear();
  const autos = cls.weapon.barrels.filter((b) => b.auto);
  if (autos.length === 0) return false;
  for (const b of autos) {
    const a = (b.angle * Math.PI) / 180;
    const ox = -Math.sin(a) * b.offset * R;
    const oy = Math.cos(a) * b.offset * R;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    const w = b.width * R;
    const L = b.length * R;
    g.poly(rot([[0, -w / 2], [L, -w / 2], [L, w / 2], [0, w / 2]], cos, sin).map((v, i) => v + (i % 2 === 0 ? ox : oy))).fill(p.barrel).stroke({ width: 1.5, color: p.outline });
    g.circle(ox, oy, 0.24 * R).fill(p.plate).stroke({ width: 1.5, color: p.outline });
    g.circle(ox, oy, 0.1 * R).fill(accent);
  }
  return true;
}

export interface ShapeLook {
  fill: number;
  inner: number;
  outline: number;
  glow: number;
  glowAlpha: number;
}

export const SHAPE_LOOKS: ShapeLook[] = [
  { fill: 0xb9c4cf, inner: 0x9aa6b2, outline: 0x3a434d, glow: 0, glowAlpha: 0 },
  { fill: 0xd98a4c, inner: 0xb86d36, outline: 0x4a2a14, glow: 0, glowAlpha: 0 },
  { fill: 0x3fbaa6, inner: 0x2d9282, outline: 0x123f38, glow: 0x3fbaa6, glowAlpha: 0.08 },
  { fill: 0x84e4ff, inner: 0x4fc3ea, outline: 0x174a5e, glow: 0x84e4ff, glowAlpha: 0.16 },
  { fill: 0xffc94d, inner: 0xff7ad9, outline: 0x5a3a10, glow: 0xffc94d, glowAlpha: 0.22 },
];

export function drawShape(g: Graphics, def: ShapeDef): void {
  const look = SHAPE_LOOKS[def.kind];
  const r = def.radius;
  g.clear();
  if (look.glowAlpha > 0) g.circle(0, 0, r * 1.45).fill({ color: look.glow, alpha: look.glowAlpha });
  g.regularPoly(0, 0, r, def.sides, Math.PI / def.sides).fill(look.fill).stroke({ width: 3, color: look.outline });
  switch (def.id) {
    case 'F1':
      g.regularPoly(0, 0, r * 0.62, 4, Math.PI / 4).fill(look.inner);
      for (let i = 0; i < 4; i++) {
        const a = Math.PI / 4 + (i * Math.PI) / 2;
        g.circle(Math.cos(a) * r * 0.68, Math.sin(a) * r * 0.68, 2).fill(look.outline);
      }
      break;
    case 'F2':
      g.regularPoly(0, 0, r * 0.45, 3, Math.PI / 3).fill(look.inner);
      break;
    case 'F3':
      g.regularPoly(0, 0, r * 0.55, 5, Math.PI / 5).fill(look.inner).stroke({ width: 2, color: look.outline, alpha: 0.6 });
      g.circle(0, 0, r * 0.16).fill(look.outline);
      break;
    case 'F4':
      for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI) / 3 + Math.PI / 6;
        g.moveTo(0, 0).lineTo(Math.cos(a) * r, Math.sin(a) * r).stroke({ width: 1.5, color: look.outline, alpha: 0.5 });
      }
      g.regularPoly(0, 0, r * 0.4, 6, Math.PI / 6).fill({ color: 0xffffff, alpha: 0.35 });
      break;
    case 'F5':
      g.regularPoly(0, 0, r * 0.7, 8, Math.PI / 8).fill(look.inner).stroke({ width: 2, color: look.outline });
      g.circle(0, 0, r * 0.32).fill(0xfff4d0).stroke({ width: 2, color: look.outline });
      break;
  }
}
